/**
 * HR Hooks
 *
 * Provides hooks for HR operations with TanStack Query:
 * - Sick leaves
 * - Vacation requests
 * - Vacation balances
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

// Types
export type SickLeaveStatus = 'active' | 'ended' | 'cancelled';
export type VacationRequestStatus = 'pending' | 'approved' | 'declined' | 'cancelled';
export type VacationType = 'annual' | 'unpaid' | 'parental' | 'study';

export interface SickLeave {
  id: string;
  employeeId: string;
  employeeName: string | null;
  employeeEmail: string | null;
  startDate: string | null;
  endDate: string | null;
  reason: string | null;
  hasMedicalCertificate: boolean;
  status: SickLeaveStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VacationRequest {
  id: string;
  employeeId: string;
  employeeName: string | null;
  employeeEmail: string | null;
  vacationType: VacationType;
  startDate: string | null;
  endDate: string | null;
  totalDays: number;
  status: VacationRequestStatus;
  notes: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  declineReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VacationBalance {
  employeeId: string;
  year: number;
  annual: { total: number; used: number; remaining: number };
  parental: { total: number; used: number; remaining: number };
  study: { total: number; used: number; remaining: number };
  updatedAt: string | null;
}

export interface LeaveHistoryItem {
  type: 'sick_leave' | 'vacation';
  id: string;
  startDate: string | null;
  endDate: string | null;
  days: number | null;
  status: string;
  reason: string | null;
  createdAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface SickLeaveFilters {
  [key: string]: string | number | boolean | undefined;
  status?: SickLeaveStatus;
  employeeId?: string;
  startDateFrom?: string;
  startDateTo?: string;
  limit?: number;
}

export interface VacationRequestFilters {
  [key: string]: string | number | boolean | undefined;
  status?: VacationRequestStatus;
  employeeId?: string;
  vacationType?: VacationType;
  startDateFrom?: string;
  startDateTo?: string;
  limit?: number;
}

interface RegisterSickLeaveData {
  employeeId: string;
  startDate: string;
  endDate?: string;
  reason?: string;
  hasMedicalCertificate?: boolean;
}

interface EndSickLeaveData {
  endDate?: string;
  notes?: string;
}

interface CreateVacationRequestData {
  employeeId: string;
  vacationType: VacationType;
  startDate: string;
  endDate: string;
  notes?: string;
}

interface ApproveVacationData {
  notes?: string;
}

interface DeclineVacationData {
  reason: string;
}

interface AdjustBalanceData {
  annual?: number;
  parental?: number;
  study?: number;
  notes?: string;
}

// Sick Leave Hooks

export function useSickLeaves(filters: SickLeaveFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.hr.sickLeaves.list(filters),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number | boolean | undefined> = {
        ...filters,
        cursor: pageParam as string | undefined,
      };
      return api.get<PaginatedResponse<SickLeave>>('/hr/sick-leaves', { params });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

export function useSickLeave(id: string) {
  return useQuery({
    queryKey: queryKeys.hr.sickLeaves.detail(id),
    queryFn: () => api.get<SickLeave>(`/hr/sick-leaves/${id}`),
    enabled: !!id,
  });
}

export function useRegisterSickLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RegisterSickLeaveData) =>
      api.post<{ success: boolean; sickLeave: SickLeave }>('/hr/sick-leaves', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.sickLeaves.all });
    },
  });
}

export function useEndSickLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EndSickLeaveData }) =>
      api.post<{ success: boolean; sickLeave: SickLeave }>(`/hr/sick-leaves/${id}/end`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.sickLeaves.all });
    },
  });
}

export function useCancelSickLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ success: boolean; sickLeave: SickLeave }>(`/hr/sick-leaves/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.sickLeaves.all });
    },
  });
}

// Vacation Request Hooks

export function useVacationRequests(filters: VacationRequestFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.hr.vacationRequests.list(filters),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number | boolean | undefined> = {
        ...filters,
        cursor: pageParam as string | undefined,
      };
      return api.get<PaginatedResponse<VacationRequest>>('/hr/vacation-requests', { params });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

export function useVacationRequest(id: string) {
  return useQuery({
    queryKey: queryKeys.hr.vacationRequests.detail(id),
    queryFn: () => api.get<VacationRequest>(`/hr/vacation-requests/${id}`),
    enabled: !!id,
  });
}

export function useCreateVacationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVacationRequestData) =>
      api.post<{ success: boolean; vacationRequest: VacationRequest }>('/hr/vacation-requests', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.vacationRequests.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.balances.all });
    },
  });
}

export function useApproveVacationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: ApproveVacationData }) =>
      api.put<{ success: boolean; vacationRequest: VacationRequest }>(
        `/hr/vacation-requests/${id}/approve`,
        data ?? {}
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.vacationRequests.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.balances.all });
    },
  });
}

export function useDeclineVacationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeclineVacationData }) =>
      api.put<{ success: boolean; vacationRequest: VacationRequest }>(
        `/hr/vacation-requests/${id}/decline`,
        data
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.vacationRequests.all });
    },
  });
}

export function useCancelVacationRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ success: boolean; vacationRequest: VacationRequest }>(`/hr/vacation-requests/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.vacationRequests.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.balances.all });
    },
  });
}

// Vacation Balance Hooks

export function useVacationBalance(employeeId: string, year?: number) {
  return useQuery({
    queryKey: queryKeys.hr.balances.detail(employeeId, year ?? new Date().getFullYear()),
    queryFn: () =>
      api.get<VacationBalance>(`/hr/balances/${employeeId}`, {
        params: year ? { year } : undefined,
      }),
    enabled: !!employeeId,
  });
}

export function useAdjustVacationBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: AdjustBalanceData }) =>
      api.put<{ success: boolean }>(`/hr/balances/${employeeId}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.balances.all });
    },
  });
}

// Leave History Hook

export function useLeaveHistory(employeeId: string, filters: { year?: number; type?: 'sick_leave' | 'vacation'; limit?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.hr.history.byEmployee(employeeId, filters),
    queryFn: () =>
      api.get<{ employeeId: string; year: number | null; history: LeaveHistoryItem[] }>(
        `/hr/leave-history/${employeeId}`,
        { params: filters }
      ),
    enabled: !!employeeId,
  });
}

// Helper functions
export function getStatusColor(status: SickLeaveStatus | VacationRequestStatus): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'declined':
    case 'cancelled':
      return 'error';
    case 'ended':
    default:
      return 'default';
  }
}

export function getVacationTypeLabel(type: VacationType): string {
  const labels: Record<VacationType, string> = {
    annual: 'Annual Leave',
    unpaid: 'Unpaid Leave',
    parental: 'Parental Leave',
    study: 'Study Leave',
  };
  return labels[type] || type;
}
